import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';
import {
  MagnifyingGlassIcon, ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon,
  FunnelIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import styles from './ActivityPage.module.css';

const SOURCES = ['', 'customer_self', 'staff_manual', 'promo_redeem', 'shopify_purchase'];
const SOURCE_LABELS = {
  '': 'All Sources',
  customer_self:    'Customer Self',
  staff_manual:     'Staff Manual',
  promo_redeem:     'Promo Code',
  shopify_purchase: 'Shopify Purchase',
};

const ACTION_LABELS = {
  create_new:      'Created',
  upgrade:         'Upgraded',
  renew:           'Renewed',
  link_to_shopify: 'Linked',
  promo_trial:     'Promo Trial',
};

const ACTION_BADGE = {
  create_new:      'badge-active',
  upgrade:         'badge-active',
  renew:           'badge-warning',
  link_to_shopify: 'badge-inactive',
  promo_trial:     'badge-warning',
};

export default function ActivityPage() {
  const [rows,       setRows]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search,     setSearch]     = useState('');
  const [source,     setSource]     = useState('');
  const [from,       setFrom]       = useState('');
  const [to,         setTo]         = useState('');
  const [loading,    setLoading]    = useState(true);
  const debounceRef = useRef(null);

  const load = useCallback((p, s, src, f, t) => {
    setLoading(true);
    adminAPI.getActivations({
      page: p, limit: 20,
      search: s || undefined,
      source: src || undefined,
      from: f || undefined,
      to:   t || undefined,
    })
      .then(res => {
        setRows(res.activations || []);
        setTotal(res.total || 0);
        setTotalPages(res.totalPages || 1);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(1, search, source, from, to); setPage(1); }, [source, from, to]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); load(1, val, source, from, to); }, 320);
  };

  const handlePage = (p) => { setPage(p); load(p, search, source, from, to); };

  const handleExport = async () => {
    try {
      const blob = await adminAPI.exportActivations({
        search: search || undefined,
        source: source || undefined,
        from:   from   || undefined,
        to:     to     || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activations-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed: ' + e.message); }
  };

  const clearDates = () => { setFrom(''); setTo(''); };

  // Pagination pages list
  const pages = [];
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div>
      <div className="page-header">
        <div><h1>Activity</h1><p>{total.toLocaleString()} VIP activation events</p></div>
        <div className="page-header-actions">
          <button className="btn-ghost" onClick={handleExport}>
            <ArrowDownTrayIcon style={{ width:15, height:15 }} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters row 1: search + source tabs */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <MagnifyingGlassIcon style={{ width:15, height:15, color:'var(--text-muted)' }} />
          <input
            className={styles.searchInput}
            placeholder="Search by phone, name, staff, or email…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
        </div>
        <div className={styles.filterRight}>
          <FunnelIcon style={{ width:14, height:14, color:'var(--text-muted)' }} />
          {SOURCES.map(s => (
            <button
              key={s}
              className={`${styles.statusTab} ${source === s ? styles.statusTabActive : ''}`}
              onClick={() => setSource(s)}
            >
              {SOURCE_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Filters row 2: date range */}
      <div className={styles.dateRow}>
        <ClockIcon style={{ width:14, height:14, color:'var(--text-muted)' }} />
        <label className={styles.dateLabel}>From</label>
        <input type="date" className={styles.dateInput} value={from} onChange={e => setFrom(e.target.value)} />
        <label className={styles.dateLabel}>To</label>
        <input type="date" className={styles.dateInput} value={to} onChange={e => setTo(e.target.value)} />
        {(from || to) && (
          <button className={styles.clearDates} onClick={clearDates}>Clear dates</button>
        )}
      </div>

      {/* Table */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="table-wrap" style={{ border:'none', borderRadius:0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Source</th>
                <th>Member</th>
                <th>Phone</th>
                <th>Scheme</th>
                <th>Expiry</th>
                <th>Staff / Reference</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({length:8}).map((_,i) => (
                  <tr key={i}>
                    {[140,90,110,180,110,70,100,140].map((w,j) => (
                      <td key={j}><div className="skeleton" style={{ height:16, width:w, borderRadius:6 }} /></td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr><td colSpan={8}>
                  <div className="empty-state">
                    <ClockIcon style={{ width:28, height:28 }} />
                    <h3>No activity yet</h3>
                    <p>Activations will appear here as they happen.</p>
                  </div>
                </td></tr>
              ) : rows.map(r => (
                <tr key={r.id}>
                  <td style={{ fontSize:12, color:'var(--text-muted)', whiteSpace:'nowrap' }}>
                    {r.timestamp ? format(new Date(r.timestamp), 'dd MMM yyyy HH:mm') : '—'}
                  </td>
                  <td>
                    <span className={`badge ${ACTION_BADGE[r.action] || 'badge-inactive'}`}>
                      {ACTION_LABELS[r.action] || r.action || '—'}
                    </span>
                  </td>
                  <td style={{ fontSize:12, color:'var(--text-muted)' }}>
                    {SOURCE_LABELS[r.source] || r.source || '—'}
                  </td>
                  <td>
                    <div style={{ fontWeight:600, fontSize:13 }}>{r.bc_member_name || '—'}</div>
                    {r.shopify_customer_email && (
                      <div style={{ fontSize:11, color:'var(--text-muted)' }}>{r.shopify_customer_email}</div>
                    )}
                  </td>
                  <td style={{ fontFamily:'monospace', fontSize:12 }}>{r.bc_phone || '—'}</td>
                  <td style={{ fontSize:12, color:'var(--text-muted)' }}>{r.bc_scheme || '—'}</td>
                  <td style={{ fontSize:12, color:'var(--text-muted)' }}>
                    {r.bc_expiry ? format(new Date(r.bc_expiry), 'dd MMM yyyy') : '—'}
                  </td>
                  <td style={{ fontSize:12 }}>
                    {r.staff_name && <div>{r.staff_name}</div>}
                    {r.reference && <div style={{ color:'var(--text-muted)', fontSize:11 }}>{r.reference}</div>}
                    {!r.staff_name && !r.reference && <span style={{ color:'var(--text-subtle)' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>
              Page {page} of {totalPages} · {total.toLocaleString()} events
            </span>
            <div className={styles.pages}>
              <button className={styles.pageBtn} disabled={page===1} onClick={() => handlePage(page-1)}>
                <ChevronLeftIcon style={{ width:14, height:14 }} />
              </button>
              {start > 1 && <><button className={styles.pageBtn} onClick={() => handlePage(1)}>1</button><span className={styles.pageEllipsis}>…</span></>}
              {pages.map(p => (
                <button key={p} className={`${styles.pageBtn} ${p===page ? styles.pageBtnActive : ''}`} onClick={() => handlePage(p)}>{p}</button>
              ))}
              {end < totalPages && <><span className={styles.pageEllipsis}>…</span><button className={styles.pageBtn} onClick={() => handlePage(totalPages)}>{totalPages}</button></>}
              <button className={styles.pageBtn} disabled={page===totalPages} onClick={() => handlePage(page+1)}>
                <ChevronRightIcon style={{ width:14, height:14 }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
